import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { getLatestDashboardActivity } from './api'
import { onBookingsChanged } from './bookingSignal'
import { onDashboardItemRead } from './dashboardReadSignal'

export type NewItemsResource = 'contacts' | 'forms' | 'orders' | 'bookings'
export type NewItemsRow = { id?: string; createdAt?: string; firstSeen?: string; seenAt?: string | null; changeSeenAt?: string | null; status?: string }
type Latest = Record<NewItemsResource, string | null>
const EMPTY: Latest = { contacts: null, forms: null, orders: null, bookings: null }
const memory = new Map<string, number>()
const timestamp = (value: string | null | undefined) => { const n = Date.parse(value ?? ''); return Number.isFinite(n) ? n : 0 }
const rowTimestamp = (row: NewItemsRow) => timestamp(row.createdAt ?? row.firstSeen)

function checkpoint(key: string): number {
  try {
    const stored = window.localStorage.getItem(key)
    const value = Number(stored ?? memory.get(key) ?? 0)
    return Number.isFinite(value) ? value : 0
  } catch { return memory.get(key) ?? 0 }
}

export function newItemsResource(section: string): NewItemsResource | null {
  if (section === 'bookingsCalendar' || section === 'bookingsList') return 'bookings'
  return section === 'contacts' || section === 'forms' || section === 'orders' ? section : null
}

type Visit = {
  active: NewItemsResource | null
  cutoff: number
  hasNew: (resource: NewItemsResource) => boolean
  acknowledge: (resource: NewItemsResource, observed: number) => void
  isRead: (resource: NewItemsResource, row: NewItemsRow) => boolean
}
const Context = createContext<Visit | null>(null)

/** Page acknowledgement is distinct from individual messages' read receipts. */
export function SectionNewItemsProvider({ projectId, token, userId, section, children }: {
  projectId: string; token: string; userId: string; section: string; children: ReactNode
}) {
  const scope = `veltica:section-visited:v1:${userId}:${projectId}:`
  const active = newItemsResource(section)
  const cutoff = useMemo(() => active ? checkpoint(scope + active) : 0, [scope, active])
  const [latestResult, setLatestResult] = useState<{ scope: string; latest: Latest } | null>(null)
  const [, setRevision] = useState(0)
  const [reads, setReads] = useState<{ token: string; ids: Set<string> }>({ token, ids: new Set() })
  const latest = latestResult?.scope === scope ? latestResult.latest : EMPTY
  useEffect(() => {
    if (!userId) return
    let alive = true
    let version = 0
    const refresh = () => {
      const request = ++version
      void getLatestDashboardActivity(projectId, token).then((data) => {
        if (alive && version === request) setLatestResult({ scope, latest: data })
      }).catch(() => {})
    }
    refresh()
    const visible = () => { if (!document.hidden) refresh() }
    const changedStorage = () => setRevision((n) => n + 1)
    const timer = window.setInterval(visible, 60_000)
    window.addEventListener('focus', visible)
    window.addEventListener('storage', changedStorage)
    document.addEventListener('visibilitychange', visible)
    const unsubscribe = onBookingsChanged((project) => { if (project === projectId) refresh() })
    const unsubscribeReads = onDashboardItemRead((event) => {
      if (event.token !== token || (event.projectId && event.projectId !== projectId)) return
      setReads((previous) => ({ token, ids: new Set([...(previous.token === token ? previous.ids : []), `${event.resource}:${event.id}`].slice(-500)) }))
      refresh()
    })
    return () => {
      alive = false; window.clearInterval(timer); unsubscribe(); unsubscribeReads()
      window.removeEventListener('focus', visible); window.removeEventListener('storage', changedStorage)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [projectId, token, userId, scope])
  const acknowledge = useCallback((resource: NewItemsResource, observed: number) => {
    if (!userId || resource !== active) return
    const key = scope + resource
    const value = Math.max(observed, timestamp(latest[resource]))
    if (value <= checkpoint(key)) return
    memory.set(key, value)
    try { window.localStorage.setItem(key, String(value)) } catch { /* Keep the visit working without persistent storage. */ }
    setRevision((n) => n + 1)
  }, [scope, active, latest, userId])
  const visit: Visit = {
    active, cutoff,
    hasNew: (resource) => Boolean(userId) && resource !== active && timestamp(latest[resource]) > checkpoint(scope + resource),
    acknowledge,
    isRead: (resource, row) => Boolean(
      (reads.token === token && row.id && reads.ids.has(`${resource}:${row.id}`))
      || (resource === 'orders' && row.seenAt)
      || (resource === 'bookings' && row.changeSeenAt)
      || (resource === 'forms' && row.status && row.status !== 'unread'),
    ),
  }
  return <Context.Provider value={visit}>{children}</Context.Provider>
}

export function useSectionNewItems() { return useContext(Context) }

/** Keep arrival dots for this visit, excluding records already read anywhere. */
export function useNewItems(resource: NewItemsResource, rows: readonly NewItemsRow[], ready = true): (row: NewItemsRow) => boolean {
  const visit = useContext(Context)
  const observed = rows.reduce((max, row) => Math.max(max, rowTimestamp(row)), 0)
  const acknowledge = visit?.acknowledge
  useEffect(() => { if (ready) acknowledge?.(resource, observed) }, [resource, observed, ready, acknowledge])
  return (row) => Boolean(ready && visit?.active === resource && rowTimestamp(row) > visit.cutoff && !visit.isRead(resource, row))
}
